import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: string;
  disabled?: boolean;
  error?: string;
  variant?: 'default' | 'minimal';
  size?: 'sm' | 'md';
  square?: boolean;
  className?: string;
  accentColor?: string;
  borderColor?: string;
}

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  error,
  variant = 'default',
  size = 'sm',
  square = false,
  className = '',
  accentColor,
  borderColor,
}: CheckboxProps) {
  const isMinimal = variant === 'minimal';
  const isMd = isMinimal && size === 'md';
  return (
    <div className={className}>
      <div
        className={`
          flex ${description ? 'items-start' : 'items-center'} ${isMinimal ? (isMd ? 'gap-3' : 'gap-1.5') : 'gap-2'} cursor-pointer
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={() => !disabled && onChange(!checked)}
      >
        <motion.button
          type="button"
          role="checkbox"
          aria-checked={checked}
          disabled={disabled}
          onClick={(e) => e.preventDefault()}
          whileTap={{ scale: disabled ? 1 : 0.9 }}
          className={
            isMinimal
              ? `${isMd ? 'w-5 h-5' : 'w-4 h-4'} ${square ? '' : 'rounded'} ${square ? 'border' : 'border-2'} flex items-center justify-center flex-shrink-0 transition-colors duration-200 focus:outline-none ${checked ? 'border-current' : error ? 'border-error bg-transparent' : 'border-current/40 bg-transparent'}`
              : `w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-2 ${checked ? 'bg-primary border-primary' : error ? 'border-error bg-transparent' : 'border-border bg-transparent hover:border-primary/50'}`
          }
          style={isMinimal && checked ? (accentColor ? { backgroundColor: accentColor, borderColor: accentColor } : { backgroundColor: 'currentColor' }) : isMinimal ? { borderColor: borderColor ?? (accentColor ? `${accentColor}66` : undefined) } : undefined}
        >
          {checked && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            >
              <Check className={`${isMd ? 'w-3.5 h-3.5' : 'w-3 h-3'} text-white`} strokeWidth={3} />
            </motion.div>
          )}
        </motion.button>
        {(label || description) && (
          <div className="flex-1 select-none">
            {label && (
              <span className={isMinimal ? 'text-sm' : 'text-sm text-text-primary'}>
                {label}
              </span>
            )}
            {description && (
              <span className={isMinimal ? 'text-sm opacity-70 mt-0.5' : 'text-sm text-text-secondary mt-0.5'}>
                {description}
              </span>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="text-sm text-error mt-1.5 ml-8">{error}</p>
      )}
    </div>
  );
}
